import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Config } from '@/Config';
import {
  getValueOrPlaceholder,
  parseDateString,
  parseMomentFromTime,
} from '@/utils/helper';
import styles from './ProductMetadataTable.module.scss';
import { IAlbum } from '@/data/models/album';
import { ProjectDto } from '@/data/models/projectsService';
import { IValidationResults } from '@/data/models/validation';
import { IChangeRequest } from '@/data/models/changeRequest';
import { Link } from 'react-router-dom';

// --- Helper Components ---
export interface ProductEntryProps {
  name: string;
  value: ReactNode;
  hasRequiredError?: boolean;

  /** Which column entry groups to when there are 2 columns and when there are 3 columns */
  column?: [twoColumns: 1 | 2, threeColumns: 1 | 2 | 3];
  redirectUrl?: string | null;
}

export function ProductEntry({
  name,
  value,
  hasRequiredError,
  column,
  redirectUrl,
}: ProductEntryProps) {
  const className = column
    ? [
        styles['entry'],
        styles[`md-col-${column[0]}`],
        styles[`lg-col-${column[1]}`],
      ].join(' ')
    : styles['entry'];

  return (
    <div className={className}>
      <div>{name}</div>
      <div className={styles['value']}>
        {hasRequiredError ? (
          <RequiredMessage />
        ) : redirectUrl ? (
          <Link to={redirectUrl}>{value}</Link>
        ) : (
          value
        )}
      </div>
    </div>
  );
}

export function RequiredMessage({
  withParenthesis,
}: {
  withParenthesis?: boolean;
}) {
  const { t } = useTranslation();

  if (withParenthesis) {
    return (
      <span className={styles['required']}>
        ({t('validationAlert.required.message')})
      </span>
    );
  }

  return (
    <span className={styles['required']}>
      {t('validationAlert.required.message')}
    </span>
  );
}

export function YesNo({ value }: { value: boolean | undefined | null }) {
  const { t } = useTranslation();
  if (value) {
    return t('product.yes');
  }
  return t('product.no');
}

export function PreOrderLabel({
  enabled,
  date,
  time,
}: {
  enabled: boolean | undefined;
  date: string | null | undefined;
  time: string | null | undefined;
}) {
  const { t } = useTranslation();

  if (enabled && !date) {
    return t('common.generic.toBeDeterminedAbbr');
  }

  if (time && date) {
    const parsedDate = parseDateString(date);
    const parsedTime = parseMomentFromTime(time);
    if (parsedDate && parsedTime) {
      return `${parsedDate} ${parsedTime.format('HH:mm')} UTC`;
    }
  } else if (date) {
    return getValueOrPlaceholder(parseDateString(date));
  }

  return getValueOrPlaceholder(undefined);
}

export function projectServiceLink(projectId: number) {
  return (
    Config.ProjectService__AppConfig__UiUrl + `/ProjectDetail/${projectId}`
  );
}

export type ProductDetailsTableProps = {
  errors: IValidationResults[];
  errorFlag: boolean;
  album: IAlbum;
  project?: ProjectDto;
  onToggleLabelInfoModal: () => void;
  onToggleParentLabelInfoModal: () => void;
  changeRequest?: IChangeRequest;
};

export function LabelEntry({
  album,
  canUserViewInternalFields,
  onToggleLabelInfoModal,
  hasRequiredError,
}: {
  album: IAlbum;
  canUserViewInternalFields: () => boolean;
  onToggleLabelInfoModal: () => void;
  hasRequiredError: (field: string) => boolean;
}) {
  const { t } = useTranslation();
  return (
    <ProductEntry
      name={t('product.editProduct.labelname')}
      value={
        canUserViewInternalFields() ? (
          <button
            className="btn btn-primary alt textLink"
            onClick={onToggleLabelInfoModal}
          >
            {album.label.name}
          </button>
        ) : (
          getValueOrPlaceholder(album.label.name)
        )
      }
      hasRequiredError={hasRequiredError('Label')}
      column={[1, 1]}
    />
  );
}

export function ParentLabelEntry({
  album,
  canUserViewInternalFields,
  onToggleParentLabelInfoModal,
  hasRequiredError,
}: {
  album: IAlbum;
  canUserViewInternalFields: () => boolean;
  onToggleParentLabelInfoModal: () => void;
  hasRequiredError: (field: string) => boolean;
}) {
  const { t } = useTranslation();
  return (
    <ProductEntry
      name={t('product.editProduct.parentLabel')}
      value={
        canUserViewInternalFields() && album.label.parentOrgName ? (
          <button
            className="btn btn-primary alt textLink"
            onClick={onToggleParentLabelInfoModal}
          >
            {album.label.parentOrgName}
          </button>
        ) : (
          getValueOrPlaceholder(album.label.parentOrgName)
        )
      }
      hasRequiredError={hasRequiredError('Label')}
      column={[1, 1]}
    />
  );
}

export function ProjectEntry({
  project,
  canUserReadProjectService,
  canUserAccessProjectServiceUi,
}: {
  project?: ProjectDto;
  canUserReadProjectService: () => boolean;
  canUserAccessProjectServiceUi: () => boolean;
}) {
  const { t } = useTranslation();

  if (!canUserReadProjectService()) {
    return null;
  }

  return (
    <ProductEntry
      name={t('product.editProduct.project')}
      value={
        project?.id && canUserAccessProjectServiceUi() ? (
          <a
            target="_blank"
            rel="noopener noreferrer"
            href={projectServiceLink(project.id)}
          >
            {project.name}
          </a>
        ) : (
          getValueOrPlaceholder(project?.name)
        )
      }
      column={[2, 3]}
    />
  );
}
